/**
 * DATA-VIZ PRIMITIVES — inline SVG, NO DEPENDENCY (proposal §7: the repo has no chart
 * library and the field timeline plot is the model).
 *
 * ===========================================================================
 * EVERY MARK HAS A TEXT TWIN — colour is never the only carrier
 * ===========================================================================
 * A stacked bar is decoration over a LIST: the legend is real text (label + count) beside
 * every segment, the SVG is `aria-hidden`, and the numbers live in `tnum` text. A reader
 * with no colour vision, a screen reader, a greyscale print and a CSV all get the same
 * facts. Segment fills are the CRF tone tokens (`stage/*`, `status/*` via `app.css`'s
 * `--color-*` mapping), so dark mode keeps the pairs the contrast suites already measure;
 * the text beside each segment is `text-fg` on `bg-surface`, never coloured text alone.
 *
 * KPI TILES are the `StageCountCards` shell (surface + line + radius/md, label then a
 * `tnum text-kpi` number) with a DELTA CHIP naming the previous window — "vs last month",
 * never an unlabelled arrow. NO sparklines: there is no daily series (§8 #19), and a
 * sparkline drawn from two points would be a picture of nothing.
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

/* ───────────────────────────────────────────────────────────────── KPI tile ── */

export function KpiTile({
  label,
  value,
  detail,
  delta,
  deltaLabel,
  testId,
}: {
  label: string;
  /** Already-rendered text (a count, a percentage, a money node) — this tile does no arithmetic. */
  value: ReactNode;
  /** A quiet second line ("of 12 waiting", the window the figure was counted over). */
  detail?: ReactNode;
  /** Signed change vs the previous window; `null` = not computable (nothing in the previous window). */
  delta?: number | null;
  /** Names the comparison — "vs last month". Required whenever `delta` is given. */
  deltaLabel?: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 flex-col gap-1 rounded-md border border-line bg-surface px-4 py-3"
    >
      <span className="truncate text-body13 font-medium text-fg-secondary">{label}</span>
      <span className="tnum text-kpi text-fg">{value}</span>
      {delta === undefined ? null : <DeltaChip delta={delta} label={deltaLabel ?? ''} />}
      {detail === undefined ? null : <span className="text-label text-fg-secondary">{detail}</span>}
    </div>
  );
}

/**
 * "+3 vs last month" / "−2 vs last month" / "no change" / "no previous window".
 * Tone pairs are the status pairs already measured AA; the SIGN is in the text, so the
 * chip reads the same in greyscale.
 */
export function DeltaChip({ delta, label, unit = '' }: { delta: number | null; label: string; unit?: string }): React.JSX.Element {
  if (delta === null) {
    return <span className="text-label text-fg-secondary">no previous window</span>;
  }
  const tone =
    delta > 0 ? 'bg-ok-bg text-ok-fg' : delta < 0 ? 'bg-warn-bg text-warn-fg' : 'bg-subtle text-fg ring-1 ring-line';
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const magnitude = Math.abs(delta);
  const text =
    delta === 0 ? `no change ${label}`.trim() : `${sign}${String(magnitude)}${unit} ${label}`.trim();
  return (
    <span
      className={cn(
        'tnum inline-flex w-fit items-center rounded-pill px-2 py-0.5 text-label font-medium',
        tone,
      )}
    >
      {text}
    </span>
  );
}

/* ─────────────────────────────────────────────────────── distribution bar ── */

export interface Segment {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  /** A `text-*` utility from the token map — the segment's tone; marks fill with `currentColor`. */
  readonly toneClass: string;
}

/**
 * A single horizontal bar split by share, with the text legend UNDER it. The bar is
 * `aria-hidden`; the legend carries every fact. Zero total → a quiet empty track and
 * the legend still lists every key at 0 (an honest zero, never an absent stage).
 */
export function DistributionBar({
  segments,
  caption,
  testId,
}: {
  segments: readonly Segment[];
  /** Screen-reader / title sentence for the figure. */
  caption: string;
  testId?: string;
}): React.JSX.Element {
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);
  // Pure geometry: each segment's start is the sum of the widths before it.
  const placed = segments.map((segment, index) => ({
    segment,
    width: total === 0 ? 0 : (segment.count / total) * 100,
    x: total === 0 ? 0 : (segments.slice(0, index).reduce((sum, s) => sum + s.count, 0) / total) * 100,
  }));
  return (
    <figure data-testid={testId} className="flex flex-col gap-2">
      <figcaption className="sr-only">{caption}</figcaption>
      <div aria-hidden="true" className="h-3 w-full overflow-hidden rounded-pill bg-subtle ring-1 ring-line">
        {total === 0 ? null : (
          <svg viewBox="0 0 100 1" preserveAspectRatio="none" className="block h-full w-full">
            {placed.map(({ segment, x, width }) => (
              <rect
                key={segment.key}
                x={x}
                y={0}
                width={width}
                height={1}
                className={cn('fill-current', segment.toneClass)}
              />
            ))}
          </svg>
        )}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((segment) => (
          <li key={segment.key} className="flex items-center gap-1.5 text-body13 text-fg">
            <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full bg-current', segment.toneClass)} />
            <span>{segment.label}</span>
            <span className="tnum font-medium">{segment.count}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

/* ───────────────────────────────────────────────────────────── count bars ── */

/**
 * One row per label, a bar scaled to the largest count, the number in text. Used for
 * the work-board columns. Zero max → every bar is an empty track.
 */
export function CountBars({
  rows,
  caption,
  testId,
}: {
  rows: readonly { key: string; label: string; count: number; toneClass?: string }[];
  caption: string;
  testId?: string;
}): React.JSX.Element {
  const max = rows.reduce((top, row) => Math.max(top, row.count), 0);
  return (
    <figure data-testid={testId} className="flex flex-col gap-1.5">
      <figcaption className="sr-only">{caption}</figcaption>
      {rows.map((row) => {
        const pct = max === 0 ? 0 : (row.count / max) * 100;
        return (
          <div key={row.key} className="grid grid-cols-[minmax(0,140px)_1fr_auto] items-center gap-3">
            <span className="truncate text-body13 text-fg">{row.label}</span>
            <div aria-hidden="true" className="h-2 w-full overflow-hidden rounded-pill bg-subtle ring-1 ring-line">
              <div
                className={cn('h-full rounded-pill bg-current', row.toneClass ?? 'text-indigo-strong')}
                style={{ width: `${String(pct)}%` }}
              />
            </div>
            <span className="tnum text-body13 font-medium text-fg">{row.count}</span>
          </div>
        );
      })}
    </figure>
  );
}
